import { Anchor, constants, useWindowSize } from "@newsela/angelou";
import Link from "next/link";
import { withTrackEvent, actionNames, objectTypes } from "@newsela/snitch";
import { useRouter } from "next/router";
import { StudentActivityMetricsEntityItem } from "@/reports/types";
import ResponseError from "@/errors/ResponseError";
import ReportTable, {
  ReportTableProps,
} from "@/reports/teacher/common/ReportTable";
import {
  GeneralTableCell,
  TextLevelTableCell,
} from "@/reports/teacher/common/ReportTable/components/TableCells";
import TableHeaderCell from "@/reports/teacher/common/ReportTable/components/TableHeaderCell";
import { formatReadingTime } from "@/reports/teacher/common/ReportTable/utils";
import formatReadingActivityClassroomData, {
  TableData,
} from "@/reports/teacher/utils/formatReadingActivityClassroomData";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import { DownloadCSVProps } from "@/reports/components/DownloadCSVButton";
import formatHrefToSupportLegacyLink from "@/utils/formatHrefToSupportLegacyLink";

interface ClassroomReadingActivityTableProps {
  tableDataList: StudentActivityMetricsEntityItem[];
  loading?: boolean | undefined;
  error?: boolean | undefined | typeof ResponseError;
  onControlsUpdate: ReportTableProps<TableData>["onControlsUpdate"];
  initialState: {
    pageSize: number;
    totalResults: number;
    pageCount: number;
  };
  downloadCSVProps?: DownloadCSVProps;
}

const AnchorWithTrackEvent = withTrackEvent(Anchor);

const ClassroomReadingActivityTable = (
  props: ClassroomReadingActivityTableProps,
) => {
  const {
    tableDataList,
    loading,
    error,
    onControlsUpdate,
    initialState,
    downloadCSVProps,
  } = props;
  const router = useRouter();
  const queryString = new URLSearchParams(
    router.query as Record<string, string>,
  ).toString();
  const formattedTableData = formatReadingActivityClassroomData(tableDataList);

  const { width: windowWidth } = useWindowSize();
  const {
    breakpoints: {
      Breakpoints: { xs },
    },
  } = constants;
  const isMobile = windowWidth < xs;

  const streamingProps = {
    actionName: actionNames.navigate,
    objectType: objectTypes.fullPage,
    streamProperties: {
      description:
        "Teacher Reporting - classroom reading activity - navigate to student data from student name",
    },
  };

  const READING_ACTIVITY_COLUMNS: ReportTableProps<TableData>["columns"] = [
    {
      accessor: "student",
      fixed: !isMobile,
      Cell: (cellProps) => {
        const { cell } = cellProps;
        const href = `${MONOLITH_ORIGIN}/reports/teacher/student-data/?${queryString}&student=${String(cell?.value?.unique_id)}`;
        return (
          <AnchorWithTrackEvent
            href={href}
            onClick={(e) => {
              e.preventDefault();
              router.push(href);
            }}
            {...streamingProps}
            __renderCustomLink={({ href, children, ...anchorProps }) => {
              return (
                <Link
                  href={formatHrefToSupportLegacyLink((href as string) || "")}
                  passHref
                  legacyBehavior
                >
                  <a {...anchorProps}>{children}</a>
                </Link>
              );
            }}
          >
            {String(cell?.value?.name)}
          </AnchorWithTrackEvent>
        );
      },
      Header: () => (
        <TableHeaderCell
          isClassroomPage
          shrinkHeader={false}
          headerTitle="Student"
        />
      ),
    },
    {
      accessor: "textLevel",
      Cell: (cellProps) => {
        const { cell } = cellProps;
        return <TextLevelTableCell cell={cell} isAvg />;
      },
      Header: () => (
        <TableHeaderCell
          isClassroomPage
          shrinkHeader={false}
          headerTitle="Text level"
          headerInfo="Average Lexile of texts viewed"
        />
      ),
    },
    {
      accessor: "views",
      Cell: (cellProps) => {
        const { cell } = cellProps;
        return <GeneralTableCell cell={cell} noValue={0} />;
      },
      Header: () => (
        <TableHeaderCell
          isClassroomPage
          shrinkHeader
          headerTitle="Views"
          headerInfo="Articles viewed"
        />
      ),
    },
    {
      accessor: "activeTime",
      Cell: (cellProps) => {
        const { cell } = cellProps;
        const activeTime = formatReadingTime(cell.value);

        return (
          <GeneralTableCell
            cell={{ ...cell, value: activeTime }}
            noValue="0 min"
          />
        );
      },
      Header: () => (
        <TableHeaderCell
          isClassroomPage
          headerTitle="Active Time"
          headerInfo="Average time on article"
        />
      ),
    },
    {
      accessor: "quizzes",
      Cell: (cellProps) => {
        const { cell } = cellProps;
        return <GeneralTableCell cell={cell} noValue={0} />;
      },
      Header: () => (
        <TableHeaderCell
          isClassroomPage
          headerTitle="Quizzes"
          headerInfo="Quizzes taken"
        />
      ),
    },
    {
      accessor: "quizScores",
      Cell: (cellProps) => {
        const { cell } = cellProps;
        const cellValue =
          typeof cell.value === "number"
            ? `${String(Math.round(cell?.value * 100))}%`
            : cell.value;

        return (
          <GeneralTableCell
            cell={{ ...cell, value: cellValue }}
            noValue="N/A"
          />
        );
      },
      Header: () => (
        <TableHeaderCell
          isClassroomPage
          headerTitle="Scores"
          headerInfo="Average quiz score"
        />
      ),
    },
    {
      accessor: "writePromptScore",
      Cell: (cellProps) => {
        const { cell } = cellProps;
        const cellValue =
          typeof cell.value === "number"
            ? String(cell.value.toFixed(1))
            : cell.value;
        return (
          <GeneralTableCell
            cell={{ ...cell, value: cellValue }}
            noValue="N/A"
          />
        );
      },
      Header: () => (
        <TableHeaderCell
          isClassroomPage
          headerTitle="Write prompts"
          headerInfo="Average score"
        />
      ),
    },
    {
      accessor: "annotations",
      Cell: (cellProps) => {
        const { cell } = cellProps;
        return <GeneralTableCell cell={cell} noValue="N/A" />;
      },
      Header: () => (
        <TableHeaderCell
          isClassroomPage
          headerTitle="Annotations"
          headerInfo="Average submitted"
        />
      ),
    },
    {
      accessor: "powerWordsScore",
      Cell: (cellProps) => {
        const { cell } = cellProps;
        const cellValue =
          typeof cell.value === "number"
            ? `${String(cell?.value)}%`
            : cell.value;
        return (
          <GeneralTableCell
            cell={{ ...cell, value: cellValue }}
            noValue="N/A"
          />
        );
      },
      Header: () => (
        <TableHeaderCell
          isClassroomPage
          headerTitle="Power words"
          headerInfo="Average score"
        />
      ),
    },
  ];

  return (
    <ReportTable
      title="Reading activity"
      description="See students' overall reading activity across texts."
      error={error}
      columns={READING_ACTIVITY_COLUMNS}
      data={formattedTableData}
      initialState={{
        hiddenColumns: [],
        ...initialState,
      }}
      loading={loading}
      downloadCSVProps={downloadCSVProps}
      onControlsUpdate={onControlsUpdate}
    />
  );
};

export default ClassroomReadingActivityTable;
